/**
 * Covert Hypnosis Mode — continuous spoken subliminal delivery engine.
 *
 * The rest of the player already has a sophisticated subliminal *transport*
 * (binaural beats, covert masking, carrier shifting, gain ceilings) and a
 * continuous non-verbal "sex bed" lane. This module is the missing *content
 * and pacing* layer: it continuously rotates short, high-impact spoken
 * hypnotic suggestion atoms on the subliminal lane while music plays, so the
 * subject keeps receiving spoken depth cues — not just erotic ambience.
 *
 * Positional audio: the subjects wear Bluetooth headphones with good range.
 * Each covert atom is placed in 3D space around the head (HRTF panner) and
 * slowly orbits, so the suggestion feels like it is arriving from a moving
 * point in the room rather than a fixed channel. This keeps it covert (never
 * a centered, consciously-attended voice) while maximizing the sense of an
 * external, surrounding presence.
 *
 * The engine is deliberately self-contained: it fetches the covert inventory
 * once, then schedules atoms on a timer while the session is playing. It does
 * not depend on the per-track overlay schedule, so it works for any music bed.
 */

import { LANE_SUBLIMINAL_1 } from "./audio-lanes.js";

// Pacing: how often a new covert atom fires while music is playing.
export const COVERT_INTERVAL_MS = 18_000;
// How far the atom's position drifts per step (radians) so it orbits slowly.
const COVERT_ORBIT_STEP = 0.9;
// Radius of the orbit around the head (meters). Close enough to feel intimate,
// far enough to stay covert under the bed.
const COVERT_RADIUS = 1.4;
// Function order for a full covert pass: deepen → condition → anchor → ratify.
const COVERT_FUNCTION_ORDER = ["deepen", "condition", "anchor", "ratify"];
// Covert speech is delivered IN-BAND (natural 300 Hz–3 kHz voice band), not on
// an ultrasonic carrier. See the Black Book of Covert Hypnosis (§0): the brain
// entrains to the MODULATION (0.5–40 Hz), not the carrier, and SSB-shifting
// speech to ~15 kHz is discarded by lossy BT codecs / drivers and, even if it
// survives, is heard as a whistle — never decoded as words. So we keep the
// carrier at 0 (in-band) and make the speech low-salience via ducking,
// band-limiting, AM, and positional orbit instead.
const COVERT_CARRIER_HZ = 0;
// Optional experimental "silent" mode: relocates speech to a near-ultrasonic
// carrier. This is THEATER for covert words (see Black Book §3) and is kept
// only behind an explicit experiment flag for A/B testing. Never the default.
const COVERT_SILENT_EXPERIMENT_HZ = 15_000;
// Covert speech sits well under the music bed so it is never a centered,
// consciously-attended voice. This is the ducking/level ceiling.
const COVERT_SPEECH_GAIN = 0.16;
// During the arousal pulse's "drop" phase the suggestion may swell slightly
// (still under the ceiling) so the words arrive with the deepening. This is
// the multiplier applied on top of COVERT_SPEECH_GAIN during a drop.
const COVERT_DROP_GAIN_BOOST = 1.6;
// When the arousal pulse is enabled, the engine fires on the "drop" phase
// (peak suggestibility) instead of a fixed timer. This is how long before the
// next expected drop we allow an early fire so atoms land at the start of the
// descent rather than mid-way.
const COVERT_DROP_LOOKAHEAD_MS = 2500;

/**
 * Return the next (or current, at the boundary) pulse drop start in the same
 * session-relative clock used by the pulse scene.
 */
export function nextCovertDropStartMs(elapsedMs, pulse = {}) {
    const elapsed = Math.max(0, Number(elapsedMs) || 0);
    const riseS = Math.max(0, Number(pulse.riseS) || 20);
    const holdS = Math.max(0, Number(pulse.holdS) || 12);
    const dropS = Math.max(0, Number(pulse.dropS) || 30);
    const restS = Math.max(0, Number(pulse.restS) || 60);
    const cycle = riseS + holdS + dropS + restS;
    if (!(cycle > 0) || String(pulse.phase || "base") === "base") return null;
    const elapsedS = elapsed / 1000;
    const positionS = elapsedS % cycle;
    const dropStartS = riseS + holdS;
    const secondsToDrop = positionS <= dropStartS
        ? dropStartS - positionS
        : cycle - positionS + dropStartS;
    return elapsed + secondsToDrop * 1000;
}

/**
 * Decide whether a covert atom may fire without mixing wall-clock and session
 * clocks. This pure helper keeps the timing contract directly testable.
 */
export function shouldFireCovertAtom({
    elapsedMs,
    lastFiredElapsedMs = Number.NEGATIVE_INFINITY,
    pulse = {},
    intervalMs = COVERT_INTERVAL_MS,
    lookaheadMs = COVERT_DROP_LOOKAHEAD_MS,
} = {}) {
    const elapsed = Math.max(0, Number(elapsedMs) || 0);
    const lastFired = Number(lastFiredElapsedMs);
    const minimumGap = elapsed - lastFired;
    const phase = String(pulse.phase || "base");
    if (phase === "base") return minimumGap >= intervalMs;
    const dropStart = nextCovertDropStartMs(elapsed, pulse);
    if (dropStart === null) return minimumGap >= intervalMs;
    const atDrop = elapsed >= dropStart - lookaheadMs && elapsed <= dropStart + 500;
    return atDrop || minimumGap >= intervalMs * 2;
}

/**
 * Build a covert delivery engine bound to the player's refs and helpers.
 *
 * @param {object} deps
 * @param {object} deps.api            API client (fetchCovert).
 * @param {object} deps.getState       () => current player state.
 * @param {object} deps.overlayContext () => overlayAudioContextRef.current.
 * @param {object} deps.ensureWorklet  async () => bool (ensure subliminal worklet).
 * @param {object} deps.ensureRouting  (lane) => routing (ensureOverlayLaneRouting).
 * @param {object} deps.applyScene     (routing, scene, t) => void.
 * @param {object} deps.resolveScene   () => scene (brainwaveLabScene).
 * @param {object} deps.resolveGain    (scene) => subliminal base gain.
 * @param {object} deps.resolvePulse   () => { phase, hz, riseS, holdS, dropS, restS }
 *                                     (arousal pulse state + geometry).
 * @param {object} deps.getElapsedMs   () => session elapsed ms (same clock the
 *                                     pulse uses, so the atom lands on the drop).
 * @param {object} deps.logPlayback    (payload) => void.
 * @param {object} deps.isPlaying      () => bool.
 * @param {object} deps.isNsfw         () => bool.
 * @param {object} deps.isMainMusic    () => bool.
 */
export function createCovertEngine(deps) {
    let inventory = [];
    let timer = null;
    let cursor = 0;
    let orbitAngle = 0;
    // This must remain in the session-relative clock returned by getElapsedMs.
    // Date.now() is a wall clock and cannot be compared with pulse geometry.
    let lastFiredElapsedMs = Number.NEGATIVE_INFINITY;
    let active = false;

    async function loadInventory() {
        try {
            const tracks = await deps.api.fetchCovert();
            // Prefer NSFW-capable, spoken atoms; keep the full pool for rotation.
            inventory = Array.isArray(tracks) ? tracks : [];
            return inventory.length;
        } catch {
            inventory = [];
            return 0;
        }
    }

    function pickNext(inDrop = false) {
        if (!inventory.length) return null;
        // During the arousal pulse's drop (peak suggestibility), prefer the
        // "deepen" atoms so the words land exactly as the brain falls into
        // deep trance. Otherwise rotate through the full function order.
        if (inDrop) {
            const deepen = inventory.filter((track) => track.function === "deepen");
            if (deepen.length) {
                const track = deepen[cursor % deepen.length];
                cursor += 1;
                return track;
            }
        }
        // Rotate through functions in order, then through atoms within each.
        const functionIndex = Math.floor(cursor / Math.max(1, inventory.length));
        const targetFunction = COVERT_FUNCTION_ORDER[functionIndex % COVERT_FUNCTION_ORDER.length];
        const candidates = inventory.filter((track) => track.function === targetFunction);
        const pool = candidates.length ? candidates : inventory;
        const track = pool[cursor % pool.length];
        cursor += 1;
        return track;
    }

    function orbitPosition() {
        orbitAngle += COVERT_ORBIT_STEP;
        if (orbitAngle > Math.PI * 2) orbitAngle -= Math.PI * 2;
        // Orbit in the horizontal plane around the head.
        return {
            x_m: Math.sin(orbitAngle) * COVERT_RADIUS,
            y_m: 0,
            z_m: -Math.cos(orbitAngle) * COVERT_RADIUS,
        };
    }

    // Resolve the current arousal-pulse state (phase, hz, and geometry).
    function currentPulse() {
        try {
            const pulse = deps.resolvePulse?.() || {};
            return {
                phase: String(pulse.phase || "base"),
                hz: Number(pulse.hz) || 0,
                riseS: Number(pulse.riseS) || 20,
                holdS: Number(pulse.holdS) || 12,
                dropS: Number(pulse.dropS) || 30,
                restS: Number(pulse.restS) || 60,
            };
        } catch {
            return { phase: "base", hz: 0, riseS: 20, holdS: 12, dropS: 30, restS: 60 };
        }
    }

    // Decide whether to fire now. With the arousal pulse enabled, the atom is
    // scheduled to coincide with the drop start (the movable element). We fire
    // when we are at (or just past) the drop start, or when the fixed-interval
    // safety net elapses so the covert lane NEVER goes silent.
    function shouldFireNow(elapsedMs) {
        const pulse = currentPulse();
        return shouldFireCovertAtom({
            elapsedMs,
            lastFiredElapsedMs,
            pulse,
        });
    }

    async function fire() {
        if (!active) return;
        if (!deps.isPlaying()) return;
        if (!deps.isNsfw()) return;
        if (!deps.isMainMusic()) return;
        const elapsedMs = deps.getElapsedMs ? deps.getElapsedMs() : 0;
        if (!shouldFireNow(elapsedMs)) return;
        const pulse = currentPulse();
        const inDrop = pulse.phase === "drop";
        const track = pickNext(inDrop);
        if (!track) return;
        const ctx = deps.overlayContext();
        if (!ctx) return;
        if (ctx.state !== "running") {
            try { await ctx.resume(); } catch { return; }
        }
        const workletReady = await deps.ensureWorklet();
        if (!workletReady) return;
        const routing = deps.ensureRouting(LANE_SUBLIMINAL_1);
        if (!routing?.shifter || !routing?.gain) return;
        const scene = deps.resolveScene();
        const t = ctx.currentTime;
        deps.applyScene(routing, scene, t);
        // Covert speech is delivered IN-BAND (carrier 0) so the brain can
        // actually decode the words. The low-salience comes from ducking,
        // band-limiting, AM, and positional orbit — not from an ultrasonic
        // carrier (which is theater; see Black Book §0/§3). The ultrasonic
        // "silent" mode is kept only behind an explicit experiment flag.
        const silentExperiment = Boolean(
            deps.getState?.().covertSilentExperiment,
        );
        const carrierHz = silentExperiment ? COVERT_SILENT_EXPERIMENT_HZ : COVERT_CARRIER_HZ;
        try {
            const carrier = routing.shifter.parameters.get("carrierShiftHz");
            carrier.cancelScheduledValues(t);
            carrier.setValueAtTime(carrierHz, t);
        } catch { /* best-effort carrier relocation */ }
        // Low-frequency tactile pulse: a 50–100 Hz bass ripple is felt in the
        // chest/sternum (Meissner's corpuscles), grounding the covert words in
        // a physical body sensation rather than a purely mental one.
        try {
            const bassPulse = routing.shifter.parameters.get("bassPulseHz");
            const bassRipple = routing.shifter.parameters.get("bassRippleDepth");
            if (bassPulse) bassPulse.setValueAtTime(60, t);
            if (bassRipple) bassRipple.setValueAtTime(0.22, t);
        } catch { /* best-effort bass pulse */ }
        // Duck the covert speech well under the music bed so it is never a
        // centered, consciously-attended voice. In-band speech at full level
        // would be audible; this ceiling keeps it at the perceptual edge.
        // During the arousal pulse's drop, the suggestion swells slightly (still
        // under the ceiling) so the words arrive WITH the deepening — the
        // contrast reinforces the descent (Black Book §5.6).
        const baseGain = deps.resolveGain(scene);
        const dropBoost = inDrop ? COVERT_DROP_GAIN_BOOST : 1;
        const ceiling = Math.min(baseGain, COVERT_SPEECH_GAIN * dropBoost);
        const covertGain = silentExperiment ? baseGain : ceiling;
        routing.subliminalBaseGain = covertGain;
        routing.gain.gain.setTargetAtTime(covertGain, t, 0.12);
        // Positional audio: place the atom in 3D space and let it orbit.
        const pos = orbitPosition();
        if (routing.panner) {
            try {
                routing.panner.positionX.setTargetAtTime(pos.x_m, t, 0.25);
                routing.panner.positionY.setTargetAtTime(pos.y_m, t, 0.25);
                routing.panner.positionZ.setTargetAtTime(pos.z_m, t, 0.25);
            } catch { /* best-effort positioning */ }
        }
        const laneAudio = deps.audioForLane ? deps.audioForLane(LANE_SUBLIMINAL_1) : null;
        if (laneAudio) {
            laneAudio.loop = false;
            laneAudio.src = track.audio_src;
            laneAudio.load();
            try { await laneAudio.play(); } catch { /* best-effort */ }
        }
        lastFiredElapsedMs = elapsedMs;
        deps.logPlayback?.({
            event_type: "covert_delivery",
            overlay_file: track.file || "",
            overlay_lane: LANE_SUBLIMINAL_1,
            overlay_type: "subliminal",
            text: track.text || "",
            function: track.function || "deepen",
            position_x: pos.x_m,
            position_z: pos.z_m,
        });
    }

    function start() {
        if (active) return;
        active = true;
        loadInventory().then((count) => {
            if (!active) return;
            if (count === 0) return;
            // Fire the first atom shortly after start, then tick. The tick is
            // fast (1 s) so we can land precisely on the arousal-pulse drop
            // window; when the pulse is off, shouldFireNow gates on the fixed
            // interval so the fast tick is harmless.
            fire();
            timer = window.setInterval(fire, 1000);
        });
    }

    function stop() {
        active = false;
        if (timer) {
            window.clearInterval(timer);
            timer = null;
        }
    }

    return { start, stop, loadInventory, getInventory: () => inventory };
}
